<script setup lang="ts">
/**
 * A non-modal window that floats over the page: it can be dragged by its
 * header, the page behind it stays usable, and it remembers where it was left.
 * Made for the popped-out timer window; chat or the assistant can use it the
 * same way. `NxModal` is always modal — this is its counterpart.
 *
 * Position: `v-model:position` when the caller wants to own it; otherwise the
 * panel keeps it itself, and with `positionKey` also in the user's settings
 * (`shell.panes.<positionKey>`, the kit's settings contract, I-2510), so it
 * reopens where the user put it. Without any position it opens at
 * `placement`. The panel is kept on screen (header always reachable), also
 * when the window shrinks.
 *
 * Keyboard: the header is a tab stop; arrow keys move the panel (Shift moves
 * further). Escape inside the panel closes it unless `closeOnEscape` is off.
 * Focus is NOT trapped — that is the point of a non-modal window.
 *
 * Shape: by default the panel is as tall as its content and the body is
 * padded. A chat-like window sets `height` and `flush`: the body then fills the
 * panel without padding and is a column its content divides itself (a thread
 * that grows, a composer underneath). `header-leading` puts an icon or a back
 * button in front of the title.
 */
import { DismissIcon, ChevronDownIcon, ChevronUpIcon } from '@blpde/nx-icons';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue';
import NxIconButton from '../IconButton/NxIconButton.vue';
import { isModalDragHandleTarget } from '../Modal/modalDrag';
import { useNxPaneState } from '../Settings/settingsSource';
import { useTableFocusClaim } from '../Table/focusScope';
import {
  clampFloatingPosition,
  initialFloatingPosition,
  storedFloatingPosition,
  type NxFloatingPanelMargin,
  type NxFloatingPanelPlacement,
  type NxFloatingPanelPosition,
} from './floatingPanel';

const props = withDefaults(
  defineProps<{
    title: string;
    /** Muted second line under the title, e.g. „2 laufend · gesamt 1:23:45“. */
    subtitle?: string;
    /** Remember the position in the user's settings (`shell.panes.<positionKey>`). */
    positionKey?: string;
    placement?: NxFloatingPanelPlacement;
    /**
     * Distance to the window edge when it opens at `placement`: one number, or
     * one per axis — e.g. to stay clear of a button in the corner.
     */
    margin?: NxFloatingPanelMargin;
    /** Panel width (CSS length). */
    width?: string;
    /** Fixed panel height (CSS length); the body fills it. Default: as tall as the content. */
    height?: string;
    /** Body without padding, laid out as a column that fills the panel. */
    flush?: boolean;
    /** Offer a button that folds the panel down to its header. */
    collapsible?: boolean;
    closeOnEscape?: boolean;
    /** Render into `body` (default). Off keeps it in place, e.g. inside a test. */
    teleport?: boolean;
    closeLabel?: string;
    collapseLabel?: string;
    expandLabel?: string;
    /** Accessible description of the header as a move handle. */
    moveHint?: string;
  }>(),
  {
    subtitle: undefined,
    positionKey: undefined,
    placement: 'bottom-right',
    margin: undefined,
    width: 'var(--nxt-panel-width-md)',
    height: undefined,
    flush: false,
    collapsible: false,
    closeOnEscape: true,
    teleport: true,
    closeLabel: 'Schließen',
    collapseLabel: 'Einklappen',
    expandLabel: 'Ausklappen',
    moveHint: 'Mit den Pfeiltasten verschieben',
  }
);

const open = defineModel<boolean>('open', { required: true });
const collapsed = defineModel<boolean>('collapsed', { default: false });
/** Optional: the caller owns the position. `null` = not placed yet. */
const positionModel = defineModel<NxFloatingPanelPosition | null>('position', { default: null });

const emit = defineEmits<{ close: [] }>();

defineSlots<{
  default?(): unknown;
  /** In front of the title: an icon badge or a back button. */
  'header-leading'?(): unknown;
  /** Replaces the title text (e.g. a status dot in front of it); stays the dialog's name. */
  title?(): unknown;
  /** Replaces the subtitle line (live figures). */
  subtitle?(): unknown;
  /** Controls next to the collapse/close buttons. */
  'header-actions'?(): unknown;
  footer?(): unknown;
}>();

const titleId = useId();
const hintId = useId();
const panelRef = ref<HTMLElement | null>(null);
const headerRef = ref<HTMLElement | null>(null);
// Opened from a table, it belongs to that table's editing wherever it renders.
useTableFocusClaim(panelRef, () => open.value);
const position = ref<NxFloatingPanelPosition | null>(positionModel.value);

watch(positionModel, (p) => {
  if (p) position.value = p;
});

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight };
}

function measure() {
  const rect = panelRef.value?.getBoundingClientRect();
  const header = headerRef.value?.getBoundingClientRect().height ?? 0;
  return { width: rect?.width ?? 0, height: rect?.height ?? 0, header };
}

function clamp(p: NxFloatingPanelPosition) {
  const size = measure();
  return clampFloatingPosition(p, { width: size.width, header: size.header }, viewport());
}

const pane = useNxPaneState<NxFloatingPanelPosition>(() => props.positionKey);
const storedPosition = computed(() => storedFloatingPosition(pane.state.value));

function setPosition(p: NxFloatingPanelPosition, persist: boolean) {
  const next = clamp(p);
  position.value = next;
  positionModel.value = next;
  if (persist) pane.patch({ x: Math.round(next.x), y: Math.round(next.y) });
}

/** First placement once the panel is rendered and has a size. */
async function place() {
  await nextTick();
  if (!panelRef.value) return;
  const start =
    position.value ??
    storedPosition.value ??
    initialFloatingPosition(props.placement, measure(), viewport(), props.margin);
  setPosition(start, false);
}

watch(
  open,
  (isOpen) => {
    if (isOpen) void place();
  },
  { immediate: true }
);

function onResize() {
  if (open.value && position.value) setPosition(position.value, false);
}

onMounted(() => window.addEventListener('resize', onResize));
onBeforeUnmount(() => window.removeEventListener('resize', onResize));

// ── Dragging ───────────────────────────────────────────────────────────────
let drag: { pointerId: number; dx: number; dy: number } | null = null;
const dragging = ref(false);

function onPointerDown(event: PointerEvent) {
  if (event.button !== 0 || !isModalDragHandleTarget(event.target)) return;
  const current = position.value ?? { x: 0, y: 0 };
  drag = {
    pointerId: event.pointerId,
    dx: event.clientX - current.x,
    dy: event.clientY - current.y,
  };
  dragging.value = true;
  headerRef.value?.setPointerCapture?.(event.pointerId);
  event.preventDefault();
  // preventDefault (no text selection while dragging) also keeps the browser
  // from focusing the header: a press on it puts the keyboard there itself, so
  // the arrow keys move the panel and Escape closes it right after.
  headerRef.value?.focus({ preventScroll: true });
}

function onPointerMove(event: PointerEvent) {
  if (!drag || event.pointerId !== drag.pointerId) return;
  setPosition({ x: event.clientX - drag.dx, y: event.clientY - drag.dy }, false);
}

function onPointerUp(event: PointerEvent) {
  if (!drag || event.pointerId !== drag.pointerId) return;
  headerRef.value?.releasePointerCapture?.(event.pointerId);
  drag = null;
  dragging.value = false;
  if (position.value) setPosition(position.value, true);
}

function onHeaderKeydown(event: KeyboardEvent) {
  if (event.target !== headerRef.value) return;
  const step = event.shiftKey ? 64 : 16;
  const delta: Record<string, [number, number]> = {
    ArrowLeft: [-step, 0],
    ArrowRight: [step, 0],
    ArrowUp: [0, -step],
    ArrowDown: [0, step],
  };
  const d = delta[event.key];
  if (!d) return;
  event.preventDefault();
  const current = position.value ?? { x: 0, y: 0 };
  setPosition({ x: current.x + d[0], y: current.y + d[1] }, true);
}

function close() {
  open.value = false;
  emit('close');
}

function onPanelKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.closeOnEscape && !event.defaultPrevented) {
    event.preventDefault();
    close();
  }
}

function toggleCollapsed() {
  collapsed.value = !collapsed.value;
  // The header stays where it is; only the body folds away.
  void nextTick(() => position.value && setPosition(position.value, false));
}

const panelStyle = computed(() => ({
  width: props.width,
  height: props.height,
  // Hidden until placed, so it never flashes in the top-left corner.
  visibility: position.value ? undefined : ('hidden' as const),
  transform: position.value ? `translate(${position.value.x}px, ${position.value.y}px)` : undefined,
}));
</script>

<template>
  <Teleport to="body" :disabled="!teleport">
    <section
      v-if="open"
      ref="panelRef"
      class="nx-floating-panel"
      :class="{
        'is-dragging': dragging,
        'is-collapsed': collapsed,
        'has-height': !!height && !collapsed,
      }"
      role="dialog"
      aria-modal="false"
      :aria-labelledby="titleId"
      :style="panelStyle"
      @keydown="onPanelKeydown"
    >
      <header
        ref="headerRef"
        class="nx-floating-panel__header"
        tabindex="0"
        :aria-describedby="hintId"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @keydown="onHeaderKeydown"
      >
        <slot name="header-leading" />
        <span class="nx-floating-panel__heading">
          <span :id="titleId" class="nx-floating-panel__title">
            <slot name="title">{{ title }}</slot>
          </span>
          <span v-if="subtitle || $slots.subtitle" class="nx-floating-panel__subtitle">
            <slot name="subtitle">{{ subtitle }}</slot>
          </span>
        </span>
        <span :id="hintId" class="nx-floating-panel__hint">{{ moveHint }}</span>
        <div class="nx-floating-panel__actions">
          <slot name="header-actions" />
          <NxIconButton
            v-if="collapsible"
            height="xs"
            data-test="collapse"
            :title="collapsed ? expandLabel : collapseLabel"
            :aria-expanded="!collapsed"
            @click="toggleCollapsed"
          >
            <ChevronDownIcon v-if="collapsed" filled class="nxt-icon-md" />
            <ChevronUpIcon v-else filled class="nxt-icon-md" />
          </NxIconButton>
          <NxIconButton height="xs" data-test="close" :title="closeLabel" @click="close">
            <DismissIcon filled class="nxt-icon-md" />
          </NxIconButton>
        </div>
      </header>
      <div v-show="!collapsed" class="nx-floating-panel__body" :class="{ 'is-flush': flush }">
        <slot />
      </div>
      <footer v-if="$slots.footer && !collapsed" class="nx-floating-panel__footer">
        <slot name="footer" />
      </footer>
    </section>
  </Teleport>
</template>

<style scoped>
.nx-floating-panel {
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--nxt-z-float);
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - var(--nxt-space-md));
  max-height: calc(100vh - var(--nxt-space-md));
  background: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  box-shadow: var(--nxt-shadow-lg);
  color: var(--nxt-fg);
  overflow: hidden;
}
.nx-floating-panel__header {
  flex-shrink: 0;
}
.nx-floating-panel.is-dragging {
  user-select: none;
  box-shadow:
    var(--nxt-shadow-lg),
    0 0 0 var(--nxt-hairline) var(--nxt-border-strong);
}

.nx-floating-panel__header {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-2xs) var(--nxt-space-2xs) var(--nxt-space-sm);
  min-height: var(--nxt-control-size-md);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
  background: var(--nxt-surface-bg);
  cursor: grab;
  touch-action: none;
  outline: none;
}
.nx-floating-panel.is-collapsed .nx-floating-panel__header {
  border-bottom: none;
}
.nx-floating-panel.is-dragging .nx-floating-panel__header {
  cursor: grabbing;
}
.nx-floating-panel__header:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-floating-panel__heading {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.nx-floating-panel__subtitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  color: var(--nxt-fg-muted);
}
.nx-floating-panel__title {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-floating-panel__hint {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.nx-floating-panel__actions {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  cursor: default;
}

.nx-floating-panel__body {
  padding: var(--nxt-space-sm);
  overflow: auto;
  min-height: 0;
}
.nx-floating-panel.has-height .nx-floating-panel__body {
  flex: 1 1 auto;
}
.nx-floating-panel__body.is-flush {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}
.nx-floating-panel__footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-top: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
</style>
